import { ShieldCheck, MapPin } from "lucide-react";
import { PageMeta } from "../components/PageMeta";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES, CONTACT } from "../data/content";

export default function About() {
  const { t, isAr } = useLanguage();
  const chapters = t("about.chapters");
  const address = isAr ? CONTACT.addressAr : CONTACT.address;

  return (
    <main>
      <PageMeta
        title="About Al Nama Importer | International Food Trading Company Dubai"
        description="Al Nama Importer is an international food and agro-commodities trading company operating from the Spice Market, Dubai. Registered with RAKEZ, United Arab Emirates."
        titleAr="من نحن | النعم للاستيراد – شركة تجارة غذائية دولية في دبي"
        descriptionAr="النعم للاستيراد شركة تجارة دولية للمنتجات الغذائية والسلع الزراعية تعمل من سوق التوابل، دبي. مسجلة لدى راكز، الإمارات العربية المتحدة."
      />
      <PageHero
        image={IMAGES.spiceMarket}
        overline={t("about.heroOver")}
        titleLines={[t("about.heroL1"), t("about.heroL2")]}
        text={t("about.heroText")}
      />

      <section className="bg-[#09121F] py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 sm:px-8 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <SectionHeading
              overline={t("about.profileOver")}
              title={
                <>
                  {t("about.titleA")} <span className="italic text-gold-gradient">{t("about.titleB")}</span>
                </>
              }
            />
            <div className="mt-8 space-y-6 text-base font-light leading-relaxed text-slate-300/90 sm:text-lg">
              <Reveal>
                <p>{t("about.p1")}</p>
              </Reveal>
              <Reveal delay={0.08}>
                <p>{t("about.p2")}</p>
              </Reveal>
              <Reveal delay={0.16}>
                <p>{t("about.p3")}</p>
              </Reveal>
              <Reveal delay={0.24}>
                <p className="rtl-quote border-l-2 border-amber-500 pl-6 font-serif text-xl italic text-emerald-50">
                  {t("about.p4")}
                </p>
              </Reveal>
            </div>
          </div>

          <div>
            <Reveal delay={0.15}>
              <div className="rounded-lg border border-amber-500/20 bg-[#0F1E18]/60 p-7">
                <ShieldCheck className="h-6 w-6 text-amber-400" strokeWidth={1.5} />
                <p className="mt-4 text-sm font-light leading-relaxed text-slate-300/85">
                  {t("about.rakezText")}
                </p>
                <p className="mt-4 flex items-start gap-2 text-xs text-slate-500">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500/70" />
                  {address}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-[#F4EFE6] py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            dark
            overline={t("about.manifOver")}
            title={
              <>
                {t("about.manifTitleA")} <span className="italic text-[#8a6d1f]">{t("about.manifTitleB")}</span>
              </>
            }
          />
          <div className="mt-16 divide-y divide-[#0B1D16]/10 border-y border-[#0B1D16]/10">
            {chapters.map((c, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div
                  data-testid={`manifesto-chapter-0${i + 1}`}
                  className="group grid grid-cols-1 gap-4 py-10 transition-colors duration-500 sm:grid-cols-[120px_1fr_1.4fr] sm:gap-10"
                >
                  <span className="font-serif text-5xl italic text-[#0B1D16]/20 transition-colors duration-500 group-hover:text-[#8a6d1f] sm:text-6xl">
                    0{i + 1}
                  </span>
                  <h3 className="font-serif text-2xl font-semibold text-[#0B1D16]">{c.t}</h3>
                  <p className="text-sm font-light leading-relaxed text-[#0B1D16]/70 sm:text-base">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-2">
        <div className="clip-frame h-72 overflow-hidden sm:h-96">
          <img src={IMAGES.spiceBaskets} alt="Wholesale spice baskets at a trading market" className="h-full w-full object-cover" loading="lazy" />
        </div>
        <div className="clip-frame-alt h-72 overflow-hidden sm:h-96">
          <img src={IMAGES.dubaiSkyline} alt="Dubai, United Arab Emirates" className="h-full w-full object-cover" loading="lazy" />
        </div>
      </section>
    </main>
  );
}
